import { defineComponent as _defineComponent } from 'vue'
import {computed, nextTick, onMounted, onUnmounted, PropType, ref, unref, watch} from "vue";
import {TopUpRotation} from "@/Enums";
import {useI18n} from "@/composables/useI18n";
import {useWebchargeSettings} from "@/composables/useWebchargeSettings";
import {useCardLimites} from "@/composables/useCardLimites";
import {useGratificationSettings} from "@/composables/useGratificationSettings";
import DateInput from "@/components/base/DateInput.vue";
import {getMinDateRestriction, isEmpty} from "@/scripts/Common/HelperFunctions";
import {
  ConfiguredWebcharge,
  dateIsAfterToday,
  hasValidAmount,
  hasValidRotationEnd,
  UpdateOrCreateConfiguredWebcharge
} from "@/components/issuers/scripts/configuredWebcharges";
import {useWebchargeRotations} from "@/components/issuers/composables/useWebchargeRotations";
import { useDateStringLocalization } from "@/composables";
import moment from "moment";


export default /*#__PURE__*/_defineComponent({
  __name: 'BulkUpdateIssuerWebchargesDialog',
  props: {
  selectedWebcharges: {
    required: true,
    type: Array as PropType<ConfiguredWebcharge[]>,
  }
},
  emits: [
  'onDialogCancel',
  'onDialogConfirm',
  'onError'
],
  setup(__props, { emit: emits }) {

const props = __props;

const {
  formatDateBasedOnLocalization
} =  useDateStringLocalization();

const amount = ref<number>(0);
const rotation = ref<TopUpRotation>(TopUpRotation.None);
const rotationEnd = ref<string>();
const nextChargingDate = ref<string>();

const webchargesToUpdate = ref<UpdateOrCreateConfiguredWebcharge[]>([]);

const isValid = ref(true);
const isDialogVisible = ref<boolean>(false);
const bulkUpdateWebchargesForm = ref();



const {
  t
} = useI18n();

const {
  error: webchargeSettingsError,
  getEnabledDates
} = useWebchargeSettings();

const {
  error: cardLimiteError,
  cardLimite
} = useCardLimites();

const {
  error: gratificationError,
  gratificationSettings
} = useGratificationSettings();

const {
  rotations
} = useWebchargeRotations();



const onHandleDialogCancel = () => {
  emits('onDialogCancel');
}

const onHandleDialogError = (error: any) => {
  emits('onError', {error});
}

const onHandleDialogConfirm = () => {
  webchargesToUpdate.value = props.selectedWebcharges.map((webcharge: ConfiguredWebcharge) =>
    ({
      id: webcharge.id,
      cardId: webcharge.cardId,
      amount: unref(amount),
      rotation: unref(rotation),
      rotationEnd: unref(isTopRotationSelected) ? undefined : unref(rotationEnd),
      nextChargingDate: unref(isTopRotationSelected) ? undefined : unref(nextChargingDate),
    }));

  emits('onDialogConfirm', webchargesToUpdate);
}

const isTopRotationSelected = computed(() =>
  rotation.value === TopUpRotation.None
);

const getCurrentAllowedAmount = () => {
  switch (rotation.value) {
    case TopUpRotation.Monthly: {
      return cardLimite.value.maxMonthlyTopUpAmount;
    }
    case TopUpRotation.Gratification: {
      return gratificationSettings.value.maximumAmountPerCharge;
    }
    default: {
      return cardLimite.value.maxCardLimit > cardLimite.value.topUpLimitPerMonth
        ? cardLimite.value.maxCardLimit
        : cardLimite.value.topUpLimitPerMonth;
    }
  }
}

const validateForm = () => {
  (bulkUpdateWebchargesForm.value as any).validate();
}

onMounted(() => {
  isDialogVisible.value = true;
  if (props.selectedWebcharges?.length !== 1) return

  amount.value = props.selectedWebcharges[0].amount;
  rotation.value = props.selectedWebcharges[0].rotation;
  rotationEnd.value = props.selectedWebcharges[0].rotationEnd;
  nextChargingDate.value = props.selectedWebcharges[0].nextChargingDate;

  // saved state might be invalid
  // validate once the child components are mounted
  nextTick(() => {
    validateForm();
  });
});

onUnmounted(() => {
  isDialogVisible.value = false;
})

watch(webchargeSettingsError, () => {
  onHandleDialogError(webchargeSettingsError)
});

watch(gratificationError, () => {
  onHandleDialogError(gratificationError)
});

watch(cardLimiteError, () => {
  onHandleDialogError(cardLimiteError)
});


return { __sfc: true,formatDateBasedOnLocalization, amount, rotation, rotationEnd, nextChargingDate, webchargesToUpdate, isValid, isDialogVisible, bulkUpdateWebchargesForm, props, t, webchargeSettingsError, getEnabledDates, cardLimiteError, cardLimite, gratificationError, gratificationSettings, rotations, emits, onHandleDialogCancel, onHandleDialogError, onHandleDialogConfirm, isTopRotationSelected, getCurrentAllowedAmount, validateForm, DateInput, getMinDateRestriction, isEmpty, dateIsAfterToday, hasValidAmount, hasValidRotationEnd, moment }
}

})